import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users } from 'lucide-react';
import ContactFormModal from '../../components/ContactFormModal';

const FINETUNE_URL = import.meta.env.VITE_FINETUNE_URL || 'https://ft.etherworks.cn';

const pricingTiers = [
  { tier: 'small', modelSize: '≤ 16B', sftLora: '$0.48', sftFull: '$0.54', dpoLora: '$1.20', dpoFull: '$1.35', highlight: false },
  { tier: 'medium', modelSize: '17B – 69B', sftLora: '$1.50', sftFull: '$1.65', dpoLora: '$3.75', dpoFull: '$4.12', highlight: true },
  { tier: 'large', modelSize: '70B – 100B', sftLora: '$2.90', sftFull: '$3.20', dpoLora: '$7.25', dpoFull: '$8.00', highlight: false },
];

const specializedModels = [
  { model: 'moonshotai/Kimi-K2-Thinking', sftLora: 15.00, dpoLora: 37.50, minCost: 60.00 },
  { model: 'moonshotai/Kimi-K2-Instruct-0905', sftLora: 15.00, dpoLora: 37.50, minCost: 60.00 },
  { model: 'moonshotai/Kimi-K2-Instruct', sftLora: 15.00, dpoLora: 37.50, minCost: 60.00 },
  { model: 'moonshotai/Kimi-K2-Base', sftLora: 15.00, dpoLora: 37.50, minCost: 60.00 },
  { model: 'deepseek-ai/DeepSeek-R1', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-R1-0528', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-V3', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-V3-0324', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-V3.1', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-V3.1-Base', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'deepseek-ai/DeepSeek-V3-Base', sftLora: 10.00, dpoLora: 25.00, minCost: 20.00 },
  { model: 'Qwen/Qwen3-Coder-480B-A35B-Instruct', sftLora: 9.00, dpoLora: 22.50, minCost: 18.00 },
  { model: 'zai-org/GLM-4.6', sftLora: 9.00, dpoLora: 22.50, minCost: 27.00 },
  { model: 'zai-org/GLM-4.7', sftLora: 9.00, dpoLora: 22.50, minCost: 27.00 },
  { model: 'zai-org/GLM-5', sftLora: 40.00, dpoLora: 100.00, minCost: 60.00 },
  { model: 'zai-org/GLM-5.1', sftLora: 40.00, dpoLora: 100.00, minCost: 60.00 },
  { model: 'meta-llama/Llama-4-Maverick-17B-128E', sftLora: 8.00, dpoLora: 20.00, minCost: 16.00 },
  { model: 'meta-llama/Llama-4-Maverick-17B-128E-Instruct', sftLora: 8.00, dpoLora: 20.00, minCost: 16.00 },
  { model: 'meta-llama/Llama-4-Maverick-17B-128E-Instruct-VLM', sftLora: 8.00, dpoLora: 20.00, minCost: 16.00 },
  { model: 'Qwen/Qwen3-235B-A22B', sftLora: 6.00, dpoLora: 15.00, minCost: 0 },
  { model: 'Qwen/Qwen3-235B-A22B-Instruct-2507', sftLora: 6.00, dpoLora: 15.00, minCost: 0 },
  { model: 'Qwen/Qwen3.5-122B-A10B', sftLora: 6.00, dpoLora: 15.00, minCost: 10.00 },
  { model: 'Qwen/Qwen3.5-397B-A17B', sftLora: 8.00, dpoLora: 20.00, minCost: 22.00 },
  { model: 'openai/gpt-oss-120b', sftLora: 5.00, dpoLora: 12.50, minCost: 6.00 },
  { model: 'meta-llama/Llama-4-Scout-17B-16E', sftLora: 3.00, dpoLora: 7.50, minCost: 6.00 },
  { model: 'meta-llama/Llama-4-Scout-17B-16E-Instruct', sftLora: 3.00, dpoLora: 7.50, minCost: 6.00 },
  { model: 'meta-llama/Llama-4-Scout-17B-16E-Instruct-VLM', sftLora: 3.00, dpoLora: 7.50, minCost: 6.00 },
];

export default function FineTunePlans() {
  const { t } = useTranslation('products');
  const [showContact, setShowContact] = useState(false);

  return (
    <>
      {/* Standard Pricing Cards */}
      <section className="pt-4 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingTiers.map((row) => (
              <div 
                key={row.tier} 
                className={`relative flex flex-col p-6 rounded-2xl border ${row.highlight ? 'border-primary shadow-xl bg-primary/[0.02]' : 'border-border bg-card'}`}
              >
                {row.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full shadow-sm">
                    Recommended
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-foreground mb-1">{t(`fine_tuning.pricing_tier_${row.tier}`)}</h3>
                </div>

                <div className="space-y-4 flex-1">
                  <div className="flex items-center justify-between py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-foreground">SFT (LoRA)</span>
                    <span className="text-sm font-semibold text-primary">{row.sftLora}<span className="text-xs text-muted-foreground">/{t('fine_tuning.pricing_per_m_tokens')}</span></span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">SFT ({t('fine_tuning.pricing_full')})</span>
                    <span className="text-sm text-foreground">{row.sftFull}<span className="text-xs text-muted-foreground">/{t('fine_tuning.pricing_per_m_tokens')}</span></span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-foreground">DPO (LoRA)</span>
                    <span className="text-sm font-semibold text-primary">{row.dpoLora}<span className="text-xs text-muted-foreground">/{t('fine_tuning.pricing_per_m_tokens')}</span></span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">DPO ({t('fine_tuning.pricing_full')})</span>
                    <span className="text-sm text-foreground">{row.dpoFull}<span className="text-xs text-muted-foreground">/{t('fine_tuning.pricing_per_m_tokens')}</span></span>
                  </div>
                </div>

                <div className="mt-8">
                  <a
                    href={`${FINETUNE_URL}/console/fine-tuning/new`}
                    className={`w-full inline-flex justify-center items-center py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      row.highlight 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                  >
                    {t('fine_tuning.pricing_start')}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-8 text-center">{t('fine_tuning.pricing_note')}</p>
        </div>
      </section>

      {/* Specialized Models */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-muted/10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">{t('fine_tuning.pricing_specialized_title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('fine_tuning.pricing_specialized_subtitle')}</p>
          </div>

          <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="text-left px-6 py-4 font-semibold text-foreground min-w-[280px]">{t('fine_tuning.pricing_model_name')}</th>
                    <th className="px-6 py-4 text-right font-semibold text-muted-foreground whitespace-nowrap">SFT (LoRA)</th>
                    <th className="px-6 py-4 text-right font-semibold text-muted-foreground whitespace-nowrap">DPO (LoRA)</th>
                    <th className="px-6 py-4 text-right font-semibold text-muted-foreground whitespace-nowrap">{t('fine_tuning.pricing_min_cost')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {specializedModels.map((row) => (
                    <tr key={row.model} className="hover:bg-muted/30 transition-colors group">
                      <td className="px-6 py-4 font-mono text-xs text-foreground group-hover:text-primary transition-colors">{row.model}</td>
                      <td className="px-6 py-4 text-right text-foreground font-medium">${row.sftLora.toFixed(2)}<span className="text-xs text-muted-foreground font-normal ml-1">/{t('fine_tuning.pricing_per_m_tokens')}</span></td>
                      <td className="px-6 py-4 text-right text-foreground font-medium">${row.dpoLora.toFixed(2)}<span className="text-xs text-muted-foreground font-normal ml-1">/{t('fine_tuning.pricing_per_m_tokens')}</span></td>
                      <td className="px-6 py-4 text-right text-muted-foreground">
                        {row.minCost > 0 ? (
                          <span className="inline-flex px-2 py-1 rounded bg-muted text-xs">${row.minCost.toFixed(2)}</span>
                        ) : (
                          <span className="text-xs">{t('fine_tuning.pricing_no_min')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-muted/50 to-muted border border-border p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-white/10" style={{ maskImage: 'linear-gradient(to bottom, transparent, black)' }} />
            <div className="relative z-10">
              <Users className="w-12 h-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">{t('fine_tuning.enterprise_title')}</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">{t('fine_tuning.enterprise_subtitle')}</p>
              <button
                onClick={() => setShowContact(true)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                {t('fine_tuning.contact_sales')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {showContact && <ContactFormModal isOpen={showContact} onClose={() => setShowContact(false)} product="fine-tuning" />}
    </>
  );
}
